/**
 * EditProductModal — single, focused modal for editing every relevant field
 * on a proposal line item. Mirrors the InkSoft pattern so the admin doesn't
 * have to jump between inline editors.
 *
 * What it edits in one place:
 *   - Color (free text — colors come from the garment's color array)
 *   - Print price (per piece) + total
 *   - Setup fee
 *   - Per-size cost / price / qty rows with live totals
 *   - Live roll-up: Product Price, Print Price, Setup Fee, Subtotal, Tax, Item Total
 *
 * Saves through the existing PATCH endpoints + a final item-level save so all
 * cascading recalcs land server-side.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { X, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';

// Legacy per-size upcharges, used only as a fallback when the item doesn't
// carry `size_costs` from SanMar (e.g. CPP items or pre-refresh proposals).
// Keep in sync with services/pricing.py.
const SIZE_UPCHARGES = { '2XL': 1.25, '3XL': 2.50, '4XL': 3.75, '5XL': 5.00 };
const COMMON_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

// Resolve wholesale cost for a specific size — prefer live SanMar per-size
// pricing on the item, fall back to base + hardcoded upcharge.
const costForSize = (size, sizeCosts, baseCost) => {
  const key = String(size || '').toUpperCase();
  if (sizeCosts && sizeCosts[key] != null && sizeCosts[key] !== '') {
    return Number(sizeCosts[key]);
  }
  return Number(baseCost) + (SIZE_UPCHARGES[key] || 0);
};

const EditProductModal = ({ proposalId, itemIndex, item, taxRate = 0, onClose, onSaved }) => {
  // Working copy of the editable fields. Initialised from the live item.
  const [color, setColor] = useState(item.color || '');
  const [imageUrl, setImageUrl] = useState(item.image_url || '');
  const [printPrice, setPrintPrice] = useState(String(item.print_cost ?? ''));
  const [setupFee, setSetupFee] = useState(String(item.setup_fee ?? ''));

  // Per-size editable rows. Each row has size key, override price ('' = auto),
  // and quantity. Start with the sizes that already have qty > 0; let admin
  // add more via the +Add Size button.
  const sizeCosts = item.size_costs || {};
  const baseCost = Number(
    item.markup_price ?? item.garment_cost ?? item.base_price ?? 0
  );
  // Markup multiplier used when we have real wholesale (size_costs) — the company garment markup
  // (default 2.1×) so admin sees consistent auto-pricing across all sizes.
  const policyMarkup = Number(item.garment_markup) > 0 ? Number(item.garment_markup) : 2.1;
  const markupMultiplier = useMemo(() => {
    const anyCost = Object.values(sizeCosts).find((v) => v != null);
    if (anyCost && Number(anyCost) > 0) {
      return policyMarkup;
    }
    return 1;
  }, [sizeCosts, policyMarkup]);

  const autoPriceForSize = (size) => {
    const wholesale = costForSize(size, sizeCosts, baseCost);
    // When we have real SanMar per-size costs, apply the multiplier; when
    // falling back to legacy upcharges, keep the raw base + upcharge (that's
    // what the original modal did).
    if (sizeCosts && sizeCosts[String(size).toUpperCase()] != null) {
      return Math.round(wholesale * markupMultiplier * 100) / 100;
    }
    return wholesale;
  };
  const sizeOverrides = item.size_price_overrides || {};
  const initialRows = useMemo(() => {
    const sizes = item.sizes || {};
    const present = Object.entries(sizes)
      .filter(([, q]) => Number(q || 0) > 0)
      .map(([s, q]) => {
        const key = String(s).toUpperCase();
        const override = sizeOverrides[key] ?? sizeOverrides[s];
        return {
          size: key,
          priceOverride: override != null && override !== '' ? String(override) : '',
          autoPrice: autoPriceForSize(key),
          costOverride: costForSize(key, sizeCosts, baseCost),
          qty: Number(q || 0),
        };
      });
    return present.length > 0 ? present : [
      { size: 'S', priceOverride: '', autoPrice: autoPriceForSize('S'), costOverride: costForSize('S', sizeCosts, baseCost), qty: 0 },
      { size: 'M', priceOverride: '', autoPrice: autoPriceForSize('M'), costOverride: costForSize('M', sizeCosts, baseCost), qty: 0 },
      { size: 'L', priceOverride: '', autoPrice: autoPriceForSize('L'), costOverride: costForSize('L', sizeCosts, baseCost), qty: 0 },
      { size: 'XL', priceOverride: '', autoPrice: autoPriceForSize('XL'), costOverride: costForSize('XL', sizeCosts, baseCost), qty: 0 },
    ];
  }, []);
  const [rows, setRows] = useState(initialRows);
  const [saving, setSaving] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [livePrices, setLivePrices] = useState(sizeCosts);

  const updateRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows((prev) => prev.filter((_, j) => j !== i));
  const addRow = () => {
    const used = new Set(rows.map((r) => r.size));
    const next = COMMON_SIZES.find((s) => !used.has(s)) || `Custom-${rows.length + 1}`;
    setRows((prev) => [...prev, {
      size: next,
      priceOverride: '',
      autoPrice: autoPriceForSize(next),
      costOverride: costForSize(next, livePrices, baseCost),
      qty: 0,
    }]);
  };

  const refreshLivePricing = async () => {
    const style = item.style || item.styleID || item.style_number;
    if (!style) {
      toast.error('Attach a SanMar product first to refresh pricing');
      return;
    }
    setRefreshingPrices(true);
    try {
      const { data } = await axios.get(`/sanmar/products/${encodeURIComponent(style)}/pricing`);
      const fresh = {};
      Object.entries(data?.sizes || {}).forEach(([k, v]) => { fresh[String(k).toUpperCase()] = Number(v); });
      if (Object.keys(fresh).length === 0) {
        toast.error('SanMar returned no pricing for this style');
        return;
      }
      setLivePrices(fresh);
      // Re-price the auto rows using new wholesale × multiplier
      setRows((prev) => prev.map((r) => ({
        ...r,
        costOverride: costForSize(r.size, fresh, baseCost),
        autoPrice: (fresh[String(r.size).toUpperCase()] != null)
          ? Math.round(fresh[String(r.size).toUpperCase()] * markupMultiplier * 100) / 100
          : r.autoPrice,
      })));
      toast.success(`Pulled live pricing from SanMar (${Object.keys(fresh).length} sizes)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Live pricing refresh failed');
    } finally {
      setRefreshingPrices(false);
    }
  };

  // Live totals — match the backend math so the modal preview matches what
  // gets saved.
  const totals = useMemo(() => {
    const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const printPerUnit = Number(printPrice || 0);
    const setupTotal = Number(setupFee || 0);
    let productTotal = 0;
    let productCost = 0;
    const sizeBreakdown = rows.map((r) => {
      const price = r.priceOverride !== '' ? Number(r.priceOverride) : r.autoPrice;
      const qty = Number(r.qty || 0);
      const line = price * qty;
      productTotal += line;
      productCost += Number(r.costOverride ?? baseCost) * qty;
      return { ...r, effectivePrice: price, lineTotal: line };
    });
    const printTotal = printPerUnit * totalQty;
    const subtotal = productTotal + printTotal + setupTotal;
    const tax = subtotal * Number(taxRate || 0);
    const itemTotal = subtotal + tax;
    // Gross margin on the garment portion only (print + setup are pure revenue
    // since decoration cost isn't tracked at the item level yet).
    const productMarginDollar = productTotal - productCost;
    const productMarginPct = productTotal > 0 ? (productMarginDollar / productTotal) * 100 : 0;
    return {
      totalQty, productTotal, productCost, productMarginDollar, productMarginPct,
      printTotal, setupTotal, subtotal, tax, itemTotal, sizeBreakdown,
    };
  }, [rows, printPrice, setupFee, taxRate, baseCost]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the patched item locally then send the full items array in
      // ONE PATCH so the server recomputes line totals + proposal total in
      // a single transaction (matches the inline editor's save path).
      const hasLiveCosts = livePrices && Object.keys(livePrices).length > 0;
      const sizes = {};
      const overrides = {};
      rows.forEach((r) => {
        const size = String(r.size).trim().toUpperCase();
        if (!size) return;
        sizes[size] = Number(r.qty || 0);
        // When the admin typed an explicit price → always persist.
        // When we have live SanMar per-size wholesale → persist the
        // computed per-size price so the backend charges each size correctly
        // (backend now treats overrides as FINAL and won't stack the legacy
        // upcharge on top).
        if (r.priceOverride !== '' && Number(r.priceOverride) !== r.autoPrice) {
          overrides[size] = Number(r.priceOverride);
        } else if (hasLiveCosts && Number(r.qty || 0) > 0) {
          overrides[size] = Number(r.autoPrice);
        }
      });
      const newPrint = Number(printPrice || 0);
      const patchedItem = {
        ...item,
        color,
        image_url: imageUrl || null,
        sizes,
        size_price_overrides: overrides,
        // Persist live SanMar per-size wholesale so future edits + refreshes
        // stay in sync (and so the backend can audit the source).
        size_costs: hasLiveCosts ? livePrices : (item.size_costs || {}),
        quantity: totals.totalQty,
        setup_fee: Number(setupFee || 0),
        print_cost: newPrint,
        // Lock auto-pricing if the admin set an explicit print override
        print_cost_locked: newPrint > 0,
        per_item: Number((Number(item.markup_price ?? item.garment_cost ?? 0) + newPrint).toFixed(2)),
        total: Number(totals.subtotal.toFixed(2)),
      };

      // Fetch latest proposal so we patch atop fresh items (avoids stomping
      // edits the AI or another admin may have made elsewhere on the page).
      const { data: latest } = await axios.get(`/proposals/${proposalId}`);
      const updatedItems = [...(latest.items || [])];
      updatedItems[itemIndex] = patchedItem;

      const { data } = await axios.patch(`/proposals/${proposalId}/items`, {
        items: updatedItems,
      });
      toast.success('Item updated');
      onSaved?.(data?.proposal || data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="edit-product-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        zIndex: 200, overflowY: 'auto', padding: '32px 16px',
      }}
    >
      <div
        style={{
          background: '#fff', borderRadius: 12, maxWidth: 720, width: '100%',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#0f172a' }}>Edit Product</h2>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              {item.brand || item.brandName || 'Custom'} | {item.style || item.styleID || item.style_number || ''}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-edit-product-modal"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Color */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'block', marginBottom: 6 }}>Color</label>
            <Input
              value={color}
              onChange={(e) => setColor(e.target.value)}
              data-testid="edit-product-color"
              placeholder="e.g. Black"
            />
            <p style={{ fontSize: 11, color: '#64748b', margin: '6px 0 0 0' }}>
              Type any color name — even ones SanMar doesn't list.
            </p>
          </div>

          {/* Product Photo Override */}
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'block', marginBottom: 6 }}>Product Photo URL</label>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 60, height: 60, background: '#f1f5f9', borderRadius: 6, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {imageUrl ? (
                  <img src={imageUrl} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.opacity = '0.2'; }} />
                ) : (
                  <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>NO IMG</span>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <Input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  data-testid="edit-product-image-url"
                  placeholder="https://…jpg or leave blank"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center' }}>
                  <input
                    type="file"
                    accept="image/*"
                    id="edit-product-file-upload"
                    data-testid="edit-product-file-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 5 * 1024 * 1024) { alert('Image too large — keep under 5MB'); return; }
                      const reader = new FileReader();
                      reader.onload = (ev) => setImageUrl(ev.target.result);
                      reader.readAsDataURL(f);
                      e.target.value = '';
                    }}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="edit-product-file-upload" style={{ fontSize: 12, color: '#2563eb', fontWeight: 600, cursor: 'pointer' }}>
                    Upload from computer
                  </label>
                  {imageUrl && (
                    <>
                      <span style={{ fontSize: 11, color: '#cbd5e1' }}>·</span>
                      <button
                        type="button"
                        onClick={() => setImageUrl('')}
                        data-testid="edit-product-image-clear"
                        style={{ fontSize: 12, color: '#b91c1c', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
                <p style={{ fontSize: 11, color: '#64748b', margin: '6px 0 0 0', lineHeight: 1.4 }}>
                  Paste a SanMar photo URL or upload your own shot when the picker's default doesn't match.
                </p>
              </div>
            </div>
          </div>

          {/* Print Price */}
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 4px 0' }}>Print Price</h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px 0', lineHeight: 1.5 }}>
              Does not include setup fee. Overriding this disables matrix auto-pricing for the item.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Unit Print Price</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: '#64748b' }}>$</span>
                  <Input
                    type="number" step="0.01" min="0"
                    style={{ width: 100 }}
                    value={printPrice}
                    onChange={(e) => setPrintPrice(e.target.value)}
                    data-testid="edit-product-print-price"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Qty</label>
                <Input style={{ width: 80, background: '#f8fafc' }} value={totals.totalQty} disabled />
              </div>
              <div style={{ marginTop: 18, color: '#64748b' }}>=</div>
              <div style={{ marginTop: 18, fontWeight: 700, color: '#0f172a' }}>{fmt(totals.printTotal)}</div>
            </div>
          </div>

          {/* Setup Fee */}
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 6px 0' }}>Setup Fee</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#64748b' }}>$</span>
              <Input
                type="number" step="0.01" min="0"
                style={{ width: 120 }}
                value={setupFee}
                onChange={(e) => setSetupFee(e.target.value)}
                data-testid="edit-product-setup-fee"
                placeholder="0.00"
              />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>flat fee added to this item</span>
            </div>
          </div>

          {/* Sizes & Quantities */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>Sizes & Quantities</h3>
              <button
                type="button"
                onClick={refreshLivePricing}
                disabled={refreshingPrices}
                data-testid="refresh-live-pricing"
                style={{ background: 'transparent', border: 'none', color: '#0ea5e9', fontSize: 12, fontWeight: 600, cursor: refreshingPrices ? 'wait' : 'pointer', padding: 0 }}
              >
                {refreshingPrices ? 'Refreshing SanMar…' : 'Refresh live SanMar pricing'}
              </button>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px 0', lineHeight: 1.5 }}>
              Cost is real SanMar wholesale per size (2XL/3XL/etc.). Leave Price blank to auto-quote at wholesale × your current margin, or type a value to lock it.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }} data-testid="edit-product-sizes-table">
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}></th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Size</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Cost</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Price</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}>Qty</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {totals.sizeBreakdown.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <button
                          onClick={() => removeRow(i)}
                          data-testid={`remove-size-${row.size}`}
                          title="Remove row"
                          style={{ background: '#f1f5f9', border: 'none', borderRadius: 4, cursor: 'pointer', padding: 4, color: '#64748b' }}
                        >
                          <Minus size={14} />
                        </button>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <select
                          value={row.size}
                          onChange={(e) => {
                            const nextSize = e.target.value;
                            updateRow(i, {
                              size: nextSize,
                              autoPrice: (livePrices[String(nextSize).toUpperCase()] != null)
                                ? Math.round(livePrices[String(nextSize).toUpperCase()] * markupMultiplier * 100) / 100
                                : baseCost + (SIZE_UPCHARGES[nextSize] || 0),
                              costOverride: costForSize(nextSize, livePrices, baseCost),
                            });
                          }}
                          data-testid={`size-select-${i}`}
                          style={{ padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 13 }}
                        >
                          {COMMON_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '6px 8px', color: '#64748b' }} data-testid={`size-cost-${row.size}`}>
                        ${Number(row.costOverride ?? baseCost).toFixed(2)}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ color: '#94a3b8' }}>$</span>
                          <Input
                            type="number" step="0.01" min="0"
                            style={{ width: 80 }}
                            value={row.priceOverride}
                            onChange={(e) => updateRow(i, { priceOverride: e.target.value })}
                            placeholder={row.autoPrice.toFixed(2)}
                            data-testid={`size-price-${row.size}`}
                          />
                        </div>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <Input
                          type="number" min="0" step="1"
                          style={{ width: 70 }}
                          value={row.qty}
                          onChange={(e) => updateRow(i, { qty: e.target.value })}
                          data-testid={`size-qty-${row.size}`}
                        />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>
                        <div>{fmt(row.lineTotal)}</div>
                        {(() => {
                          const price = Number(row.effectivePrice || 0);
                          const cost = Number(row.costOverride ?? baseCost);
                          if (price <= 0 || cost <= 0) return null;
                          const marginPct = ((price - cost) / price) * 100;
                          const marginDollar = price - cost;
                          const bg = marginPct >= 50 ? '#dcfce7' : marginPct >= 30 ? '#fef9c3' : '#fee2e2';
                          const fg = marginPct >= 50 ? '#166534' : marginPct >= 30 ? '#854d0e' : '#991b1b';
                          return (
                            <div
                              data-testid={`size-margin-${row.size}`}
                              title={`Gross margin: ($${price.toFixed(2)} price − $${cost.toFixed(2)} cost) ÷ price`}
                              style={{
                                display: 'inline-block', marginTop: 4, padding: '1px 6px',
                                background: bg, color: fg, borderRadius: 10, fontSize: 10,
                                fontWeight: 700, letterSpacing: '0.02em',
                              }}
                            >
                              {marginPct >= 0 ? `+${marginPct.toFixed(0)}%` : `${marginPct.toFixed(0)}%`} · ${marginDollar.toFixed(2)}
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              onClick={addRow}
              data-testid="add-size-row"
              style={{
                marginTop: 10, background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#0ea5e9', fontSize: 13, fontWeight: 600, display: 'inline-flex',
                alignItems: 'center', gap: 4, padding: '4px 0',
              }}
            >
              <Plus size={14} /> Add Size
            </button>
          </div>

          {/* Totals roll-up */}
          <div style={{ borderTop: '2px solid #e2e8f0', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 14 }}>
            <Row label="Total Quantity" value={totals.totalQty} highlight={false} />
            <Row label="Product Price" value={fmt(totals.productTotal)} />
            <Row label="Print Price" value={fmt(totals.printTotal)} />
            <Row label="Setup Fee" value={fmt(totals.setupTotal)} />
            {totals.productCost > 0 && (() => {
              const pct = totals.productMarginPct;
              const bg = pct >= 50 ? '#dcfce7' : pct >= 30 ? '#fef9c3' : '#fee2e2';
              const fg = pct >= 50 ? '#166534' : pct >= 30 ? '#854d0e' : '#991b1b';
              return (
                <div
                  data-testid="product-margin-total"
                  title={`Garment revenue ($${totals.productTotal.toFixed(2)}) − wholesale cost ($${totals.productCost.toFixed(2)}) ÷ revenue. Print + setup are pure revenue and not included.`}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, background: bg, color: fg, padding: '4px 8px', borderRadius: 6, marginTop: 4 }}
                >
                  <span style={{ fontWeight: 700, letterSpacing: '0.02em' }}>Garment Margin</span>
                  <span style={{ fontWeight: 700 }}>+${totals.productMarginDollar.toFixed(2)} · {pct.toFixed(0)}%</span>
                </div>
              );
            })()}
            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: 6 }}>
              <Row label="Item Subtotal" value={fmt(totals.subtotal)} bold />
              {totals.tax > 0 && <Row label={`Tax (${(taxRate * 100).toFixed(2).replace(/\.?0+$/, '')}%)`} value={fmt(totals.tax)} />}
              <Row label="Item Total" value={fmt(totals.itemTotal)} bold large />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={saving} data-testid="cancel-edit-product">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-edit-product" style={{ background: '#0f172a', color: '#fff' }}>
            {saving ? 'Saving…' : 'Update'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, bold = false, large = false }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontWeight: bold ? 700 : 400, fontSize: large ? 16 : 14 }}>
    <span style={{ color: bold ? '#0f172a' : '#475569' }}>{label}</span>
    <span style={{ color: bold ? '#0f172a' : '#0f172a' }}>{value}</span>
  </div>
);

export default EditProductModal;
